import React from "react";

/**
 * Numbered step indicator for the sequence builder wizard. Deliberately a port of the
 * job-role creation flow's progress bar (CreateJDSmartView's renderProgressBar) so the two
 * wizards read as one product: same spacing, same green completed/active circles, same
 * `>>` separators and pink→blue gradient rail.
 *
 * The step count is dynamic (2 or 3) since the Questions step only exists when screening
 * is on, so the rail width is derived rather than table-driven the way JD's is.
 *
 * Props: steps [{ key, label }], current (1-based), onStepClick(number) — only fires for
 * already-completed steps (no forward jumping past validation).
 */
const SequenceStepIndicator = ({ steps, current, onStepClick }) => {
    // JD leaves a sliver of rail filled on the first step rather than showing an empty
    // track, and stops just short of full on the last one until the wizard is submitted.
    const progressWidth = current <= 1 ? 6 : Math.min((current / steps.length) * 100, 95);

    return (
        <div className="w-full px-4 py-8">
            <div className="px-12 mx-auto">
                <div className="flex items-center justify-between mx-10">
                    {steps.map((step, index) => {
                        const num = index + 1;
                        const isCompleted = num < current;
                        const isActive = num === current || isCompleted;
                        const clickable = isCompleted;

                        return (
                            <React.Fragment key={step.key}>
                                <button
                                    type="button"
                                    disabled={!clickable}
                                    onClick={() => clickable && onStepClick(num)}
                                    className={`flex items-center gap-4 min-w-0 ${clickable ? "cursor-pointer" : "cursor-default"}`}
                                >
                                    <div
                                        className={`
                                            w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors duration-200 shrink-0
                                            ${
                                                isActive
                                                    ? "border-[#069855] text-[#069855]"
                                                    : "border-[#575B72] text-[#575B72] bg-transparent"
                                            }
                                        `}
                                    >
                                        {num}
                                    </div>

                                    <div className="min-w-0">
                                        <div
                                            className={`text-base transition-colors duration-200 truncate ${
                                                isActive && isCompleted
                                                    ? "text-[#069855]"
                                                    : isActive
                                                      ? "dark:text-white text-textPrimary"
                                                      : "text-[#575B72]"
                                            }`}
                                        >
                                            {step.label}
                                        </div>
                                    </div>
                                </button>

                                {index < steps.length - 1 && (
                                    <div className="flex items-center flex-shrink-0">
                                        <span
                                            className={`h-6 w-6 transition-colors duration-200 ${
                                                isCompleted ? "text-[#069855]" : "text-[#575B72]"
                                            }`}
                                        >
                                            {`>>`}
                                        </span>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
                <div className="w-full bg-gray-700 h-0.5 rounded-full mt-6">
                    <div
                        className="bg-gradient-to-r from-pink-500 to-blue-500 h-0.5 rounded-full transition-all duration-300"
                        style={{ width: `${progressWidth}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

export default SequenceStepIndicator;
